import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Preview, FooterButton, SharePopup, AddToCollections } from '../common';
import { openCuratorRoom } from '../helpers/urlHelper';
import { useParams } from 'react-router-dom';
import {
  apiGetPresetCameraAngle,
  apiGetRooms,
  apiToggleFavorite,
} from '../helpers/api';
import _ from 'lodash';

import { icons } from '../assets';
import { TEMPLATES_ROUTE, TEMPLATES_ROUTE_V2 } from '../constants/routePathConstants';
import { LIVE_FURNISH } from '../constants/projectConstants';
import { MODULE_TYPES } from '../constants/constants';
import { successToast } from '../helpers/toastHelper';
import { getLocalStorage, handleScrollPage, removeLocalStorage, setTitle } from '../helpers/jsHelper';
import { useFilterContext } from "../contexts/FilterContext";
import { setTemporaryPreventBackupPopup } from '../redux/slicers/admin/curatorSlicer';
import { useDispatch } from 'react-redux';
import { checkIfMemberHaveEditorAccess, useUserContext } from '../contexts/UserContext';
import { RENDER_IMAGE_TYPE } from '../constants/renderPreviewConstants';
import { Mixpanel, MxEvent } from '../common/Mixpanel';
import { CURATOR_SETTING } from '../config/indexDB/schema';
import { useSwitchOrgToggle } from "../hooks/useRedirectPageOnSwitchOrg";

const {
  ArchiveIcon,
  CollectionIcon,
  EditIcon,
  ImagineLogo,
  FullScreenIcon,
  HeartLineIcon,
  HeartIcon,
  HeadsetIcon,
  ShareIcon,
} = icons;

function TemplatesDetails() {
  const dispatch = useDispatch();
  const [loadPage, switchOrgToggle] = useSwitchOrgToggle(false);
  const { currentUser, isGuestLogin } = useUserContext();
  const currentOrg = currentUser?.organization;
  const { roomId } = useParams();
  const [data, setData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeCameraId, setActiveCameraId] = useState(null);
  const [sidebarCards, setSidebarCards] = useState([]);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favouriteLoading, setFavouriteLoading] = useState(false);
  const [roomInfo, setRoomInfo] = useState({});
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const filterCtx = useFilterContext();

  useEffect(() => {
    if (loadPage) {
      switchOrgToggle()
    }
  },[currentOrg])

  useEffect(async () => {
    setLoading(true);

    const resRoom = await apiGetRooms(`id=${roomId}`);
    if (resRoom) {
      if(resRoom.results.length > 0) {
        setRoomInfo(resRoom.results[0]);
        setTitle(resRoom.results[0].name);
      }
      setLoading(false);
    }

    return () => {
      setTimeout(() => {
        window.$restrictFilter = null
      }, 500)
    }
  }, []);

  useEffect(async () => {
    filterCtx.setBreadcrumbs(null);
    setLoading(true);

    const res = await apiGetPresetCameraAngle(roomId);
    if (res) {
      if (res.results.length > 0) {
        setData(res.results?.filter(a => a?.file !== null)?.sort((a,b) => Number(a?.sequence_number) - Number(b?.sequence_number)));
        setLoading(false);
      }
    }

  }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      const isFilmicImgType = roomInfo?.img_type === RENDER_IMAGE_TYPE.FILMIC;
      setSidebarCards(_.map(data, (d) => {
        return { 
          ...d, 
          name: d.cameraName, 
          thumbnail: d.thumbnail !== null ? isFilmicImgType ? (d?.filmic_thumbnail || d?.thumbnail) : d?.thumbnail : isFilmicImgType ? d?.filmic_file : d?.file 
        }
      }));
      setSlides(_.map(data, (d) => isFilmicImgType ? d?.filmic_file : d.file));
      !favouriteLoading && setActiveCameraId(data[0].id);
    }
  }, [data, roomInfo]);

  
  async function handleFavorite() {
    setFavouriteLoading(true);
    const favorite = !roomInfo?.is_favourite;
    await apiToggleFavorite(roomId, favorite, MODULE_TYPES.ROOM).then((res) => {
      if (res.status) {
        setRoomInfo({
          ...roomInfo,
          is_favourite: favorite
        });
        
        successToast(`${favorite ? 'Added to' : 'Removed from'} favourite`);

        // successToast(
        //   `This ${pluralize.singular(MODULE_TYPES.ROOM)} has been ${
        //     favorite ? 'favorited' : 'unfavorited'
        //   }.`
        // );
        setTimeout(() => {
          setFavouriteLoading(false);
        }, 300);
      }
    });
  }

  const handleEditClick = () => {

    if(!checkIfMemberHaveEditorAccess()) return;

    const camera = sidebarCards.find(card => card.id === activeCameraId)
    const curatorPropId = searchParams.get('curatorProp');

    Mixpanel.track(true, MxEvent.EDIT_IN_3D_TEMPLATE, {
      template_id: roomId,
      template_name: roomInfo?.name,
      template_angle_name: camera?.name,
    });

    dispatch(setTemporaryPreventBackupPopup({
      type: 'room',
      id: roomId,
    }));
    
    openCuratorRoom(roomId, { cameraName: camera?.name, propId: curatorPropId });
    Mixpanel.track(true,MxEvent.CURATOR_STARTED,{
        "Curator project type": "template",
        "Curator opened from": "edit button on template details page",
        "Project start camera": camera?.name,
        "Room id": roomInfo?.id,
        "Room name": roomInfo?.name,
        "Room isFavourite": roomInfo?.is_favourite,
        "Room creator id": roomInfo?.member_details?.id,
        "Room creator name": roomInfo?.member_details?.name,
        "Room creator email": roomInfo?.member_details?.email,
        "Room created at": roomInfo?.created_at,
    })
  }

  const actions = [
    {
      element: FooterButton,
      footerButton: true,
      title: 'Edit in 3D',
      icon: ImagineLogo,
      clickFunction: handleEditClick,
      seq: 1,
    },
    {
      element: SharePopup,
      footerButton: true,
      icon: ShareIcon,
      title: 'Share',
      onSuccess: () => {},
      seq: 2,
      modelType: 3
    },
    {
      element: AddToCollections,
      footerButton: true,
      icon: CollectionIcon,
      title: 'Add to Collection',
      selectedList: [roomInfo],
      onSuccess: () => {},
      cardType: 'templates',
      seq: 3,
    },
    {
      element: FooterButton,
      footerButton: true,
      title: 'Favourite',
      icon: roomInfo?.is_favourite ? HeartIcon : HeartLineIcon,
      clickFunction: handleFavorite,
      is_favourite: roomInfo?.is_favourite,
      loading: favouriteLoading,
      seq: 4,
    },
    {
      element: FooterButton,
      footerButton: false,
      icon: FullScreenIcon,
      title: 'Full Screen',
      seq: 5,
    },
    {
      element: FooterButton,
      footerButton: false,
      title: 'Help',
      icon: HeadsetIcon,
      // onClick: () => alert("info"),
      seq: 6,
    },
  ];

  const updatedAt = useMemo(() => {
    if (!data?.updated_at) return null;
    return new Date(data?.updated_at).getTime();
  }, [data?.updated_at]);

  const onCloseTemplateDetail = () => {
    // TOOD: handle all close cases here so we're redirecting to previous page
    // if(window.history.length > 1){ // validating history length > 1, because nothing is happening if the history lenght is 1
    //   history.back();
    //   return;
    // }
    if (window.$previousURL) {
      window.history.length ? history.back() : navigate(window.$previousURL)
      window.$previousURL = null
      setTimeout(() => {
        window.$restrictFilter = null
        handleScrollPage()
      },500)
      return
    }
    setTimeout(() => {
      window.$restrictFilter = null
      handleScrollPage()
    },500)
    navigate(`${TEMPLATES_ROUTE_V2}${window?.location?.search}`);
  }

  return (
    <>
      <Preview
        id={roomInfo?.id}
        title={roomInfo?.name}
        activeCameraName={sidebarCards?.find(a => a?.id === activeCameraId)?.cameraName}
        roomImage={roomInfo?.thumbnail}
        actions={isGuestLogin ? actions?.slice(0,1) || [] : actions}
        onClose={onCloseTemplateDetail}
        loading={loading}
        updatedAt={updatedAt}
        userName="Sasha"
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        activeCameraId={activeCameraId}
        setActiveCameraId={setActiveCameraId}
        sidebarCards={sidebarCards}
        // slides={slides}
        slides={_.map(slides, (s) => (s ? s : ''))}
        currentOrgDetails={LIVE_FURNISH}
      />
      {(data.length === 0 && roomInfo?.thumbnail === null) ? (
        <div
          style={{
            position: 'fixed',
            width: 'calc(100vw + 232px)',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            left: 0,
            top: 0,
          }}>
          <h3>{roomInfo?.name}</h3>
        </div>
      ) : null}
    </>
  );
}

export default TemplatesDetails;
